import type { Patrol } from '@local/common-lib';
import { formatDurationBetween, formatPeople } from '@local/common-lib';
import { Box, Stack, Typography } from '@mui/material';
import type { FeatureCollection, Point } from 'geojson';
import type { LngLat } from 'mapbox-gl';
import type { FC } from 'react';
import { Popup, useMap } from 'react-map-gl/mapbox';
import { DateTimeFormat } from '../../components/date/date-format.js';
import { PointsLayer } from '../../components/map/layers/points.js';
import { MapImage } from '../../components/map/map-image.js';
import accident from './svg/accident.svg';
import alarmActivated from './svg/alarm-activated.svg';
import fire from './svg/fire.svg';
import graffiti from './svg/graffiti.svg';
import medicalAssistance from './svg/medical-assistance.svg';
import people from './svg/people.svg';
import requestedCheckup from './svg/requested-checkup.svg';
import siteCheck from './svg/site-check.svg';
import vehicleCheck from './svg/vehicle-check.svg';

interface Props {
  onClick?: (latLng: LngLat, props: Record<string, unknown>) => void;
  onUnselect?: () => void;
  points: FeatureCollection<Point, Record<string, unknown>>;
  selected?: Patrol.Incident & { _instance: string; patrolId: string };
}

export const IncidentsLayer: FC<Props> = ({
  onClick,
  onUnselect,
  points,
  selected,
}) => {
  const { current: map } = useMap();

  return (
    <>
      <MapImage id="accident" size={100} svg={accident} />
      <MapImage id="alarm-activated" size={100} svg={alarmActivated} />
      <MapImage id="fire" size={100} svg={fire} />
      <MapImage id="graffiti" size={100} svg={graffiti} />
      <MapImage id="medical-assistance" size={100} svg={medicalAssistance} />
      <MapImage id="people" size={100} svg={people} />
      <MapImage id="requested-checkup" size={100} svg={requestedCheckup} />
      <MapImage id="site-check" size={100} svg={siteCheck} />
      <MapImage id="vehicle-check" size={100} svg={vehicleCheck} />
      <PointsLayer id="incidents" onClick={onClick} points={points} />
      {selected?.location?.longitude && selected.location.latitude && (
        <Popup
          anchor="bottom"
          className="popup"
          key={selected._instance}
          latitude={selected.location.latitude}
          longitude={selected.location.longitude}
          offset={10}
          onClose={() => onUnselect?.()}
          onOpen={() => {
            if (
              !map ||
              !selected.location?.longitude ||
              !selected.location.latitude
            ) {
              return;
            }
            const { y } = map.project([
              selected.location.longitude,
              selected.location.latitude,
            ]);
            if (y < 100) {
              const center = map.getCenter();
              const centerPoint = map.project(center);
              map.flyTo({
                center: map.unproject([centerPoint.x, centerPoint.y - 200]),
                zoom: map.getZoom() - 1,
              });
            }
          }}
        >
          <Box>
            <Stack direction="row" spacing={3}>
              <Typography fontWeight="bold" variant="body1">
                {selected.what}
              </Typography>
            </Stack>
            <Stack>
              <Typography fontStyle="italic" variant="caption">
                <DateTimeFormat date={selected.when} />
              </Typography>
              {selected.whatDetails ? (
                <Typography sx={{ textWrap: 'balance' }} variant="body2">
                  {selected.whatDetails}
                </Typography>
              ) : null}
              {selected.location.notes ? (
                <Typography sx={{ textWrap: 'balance' }} variant="body2">
                  {selected.location.notes}
                </Typography>
              ) : null}
              {selected.people ? (
                <Typography>People: {formatPeople(selected.people)}</Typography>
              ) : null}
              {selected.peopleDetails ? (
                <Typography sx={{ textWrap: 'balance' }} variant="body2">
                  {selected.peopleDetails}
                </Typography>
              ) : null}
              {selected.whenFinished ? (
                <Typography color="text.secondary" variant="body2">
                  Duration:{' '}
                  {formatDurationBetween(selected.when, selected.whenFinished)}
                </Typography>
              ) : null}
            </Stack>
          </Box>
        </Popup>
      )}
    </>
  );
};
